import classNames from 'classnames';
import { useDragSource, useDropTarget, useHoverPopover } from '@sorrem-client/ui/hooks';
import { forwardRef, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import type { GearComparison, ItemRarity } from '@sorrem/shared';
import { ItemSlotMarks } from '../ItemSlotMarks';
import styles from './HotkeySlot.module.scss';

export interface HotkeySlotProps {
    /** Text on the label plate under the frame, such as a hotkey. Empty leaves the plate blank. */
    label: string;
    /** Accessible name of the frame. */
    ariaLabel: string;
    /** Key that presses the frame, for assistive tech. */
    ariaKeyShortcuts?: string;
    /** Called when the frame is clicked. */
    onClick?: () => void;
    /** Called when the frame is right-clicked; the browser menu is suppressed. */
    onSecondaryClick?: (anchor: HTMLElement) => void;
    /**
     * Called when the frame is pressed and let go (or the pointer leaves it),
     * for actions that must be held. Used in place of `onClick`.
     */
    onPressStart?: () => void;
    onPressEnd?: () => void;
    /** Makes the frame draggable (carrying what it holds); called when a drag starts from it. */
    onDragStart?: () => void;
    /** Makes the frame a drop target; called when something dragged is dropped on it. */
    onItemDrop?: () => void;
    /** Makes the label plate its own button, separate from the frame. */
    onLabelClick?: () => void;
    /** Accessible name of the label plate when it is a button. */
    labelAriaLabel?: string;
    /** Lights the label plate, e.g. while it waits for a key. */
    isLabelActive?: boolean;
    /** Marks the frame as the current selection. */
    isSelected?: boolean;
    /** Marks the frame as a valid target for the current selection. */
    isHighlighted?: boolean;
    /** What the frame holds, shown in a popover while the pointer rests on the slot. None when unset. */
    popoverContent?: ReactNode;
    /** The rarity of the item it holds, shown as the tier's light and badge on the frame (#341). */
    rarity?: ItemRarity;
    /** How the item it holds compares with what is worn (#293, #341), shown as chevrons in a badge on the frame; none when about even. */
    comparison?: GearComparison;
    className?: string;
    children?: ReactNode;
}

/**
 * A square frame with a label plate under it, drawn from the Loadout's
 * action-slot art. Fills the width it is given and keeps the art's aspect
 * ratio; the label scales with the slot. Its ref is the root, e.g. to anchor a
 * menu to the slot.
 */
export const HotkeySlot = forwardRef<HTMLDivElement, HotkeySlotProps>(function HotkeySlot({
    label,
    ariaLabel,
    ariaKeyShortcuts,
    onClick,
    onSecondaryClick,
    onPressStart,
    onPressEnd,
    onDragStart,
    onItemDrop,
    onLabelClick,
    labelAriaLabel,
    isLabelActive = false,
    isSelected = false,
    isHighlighted = false,
    popoverContent,
    rarity,
    comparison,
    className,
    children,
}, ref) {
    const hoverPopover = useHoverPopover(popoverContent, ref);
    const dragSource = useDragSource(onDragStart === undefined ? undefined : { cursor: 'move', onStart: onDragStart });
    const dropTarget = useDropTarget(onItemDrop === undefined ? undefined : { onDrop: onItemDrop });

    return (
        <div {...hoverPopover} className={classNames(styles.hotkeySlot, isSelected && styles.selected, isHighlighted && styles.highlighted, className)}>
            <button
                type='button'
                className={styles.frame}
                onClick={onClick}
                onContextMenu={onSecondaryClick === undefined ? undefined : (event: MouseEvent): void => {
                    event.preventDefault();
                    onSecondaryClick(event.currentTarget as HTMLElement);
                }}
                onPointerDown={onPressStart === undefined && dragSource.onPointerDown === undefined ? undefined : (event: PointerEvent<HTMLElement>): void => {
                    dragSource.onPointerDown?.(event);
                    if (event.button === 0) {
                        onPressStart?.();
                    }
                }}
                onPointerUp={onPressEnd}
                onPointerLeave={onPressEnd}
                onPointerCancel={onPressEnd}
                {...dropTarget}
                aria-label={ariaLabel}
                aria-pressed={isSelected || undefined}
                aria-keyshortcuts={ariaKeyShortcuts === '' ? undefined : ariaKeyShortcuts}
            >
                <span className={styles.content}>{children}</span>
                {/* Inside the button, so a click on a badge presses the frame. */}
                <ItemSlotMarks rarity={rarity} comparison={comparison} className={styles.marks} />
            </button>
            {onLabelClick === undefined
                ? <span className={styles.plate} aria-hidden='true'>{label}</span>
                : (
                    <button
                        type='button'
                        className={classNames(styles.plate, styles.plateButton, isLabelActive && styles.active)}
                        onClick={onLabelClick}
                        aria-label={labelAriaLabel}
                        aria-pressed={isLabelActive}
                    >
                        {label}
                    </button>
                )}
        </div>
    );
});
